import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Iconify from 'src/components/Iconify';
import DrawerNegocioStagesPipeline from './DrawerNegocioStagesPipeline';
import NegocioInfoRow from './NegocioInfoRow';
import SemOpcoesCta from './SemOpcoesCta';

// Telas onde cada lista de opções do negócio é cadastrada. Origem, status e tags
// são abas da mesma página (deep-link via `?tab=`); campanhas têm tela própria.
const CONFIG_FUNIL = '/dashboard/configuracoes/funil-vendas';
const CADASTRO_CAMPANHAS = '/dashboard/marketing/campanhas';
import { useDispatch } from 'react-redux';

// Actions
import v2_actionPesquisarContatos from 'src/actions/v2/contatos/v2_actionPesquisarContatos';
import v2_actionPesquisarContatosEmpresa from 'src/actions/v2/contatos/v2_actionPesquisarContatosEmpresa';
import v2_actionCriarContatoEmpresa from 'src/actions/v2/contatos/v2_actionCriarContatoEmpresa';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import v2_actionEditarLead from 'src/actions/v2/crm/v2_actionEditarLead';

import { useSnackbar } from 'notistack';
import { Input } from 'src/@/components/ui/input';
import { formatMoedaBR, maskMoedaBR, parseMoedaBR } from 'src/utils/functions';

import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "src/@/components/ui/select";
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { setChatInfo, limparChats, getMensagensWhatsappDb, getChatInfoByChatId } from 'src/redux/slices/chat';
import { Button } from 'src/@/components/ui/button';
import { DropdownMenuContent, DropdownMenuTrigger, DropdownMenu } from 'src/@/components/ui/dropdown-menu';
import { Separator } from 'src/@/components/ui/separator';
import { Card, CardContent } from 'src/@/components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from 'src/@/components/ui/accordion';




// ----------------------------------------------------------------------
// COMPONENTE: MenuAlterarFunil
// ----------------------------------------------------------------------

const MenuAlterarFunil = ({ anchorEl, onOpen, configCrmProps, onClose, open, cliente, user, onConfirm }) => {
    const [pipelineId, setPipelineId] = useState(null);
    const [pipelineStageId, setPipelineStageId] = useState(null);
    const [configCrm, setConfigCrm] = useState(configCrmProps?.configCrmProps);

    const buscarConfigCrm = async (querys) => {
        try {
            const result = await v2_actionConfigCrm(cliente._id, querys);
            if (result.data.data) {
                setConfigCrm(result.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={onOpen}>
            <DropdownMenuTrigger asChild>
                <Button 
                    variant="outline" 
                    size="sm"
                    className="h-7 w-7 p-0 "
                >
                    <Iconify width={14} height={14} icon="mdi:edit" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="start" className="w-[320px] p-0 bg-white border-gray-200 dark:bg-[#222529] dark:border-[#383838]">
                <div className="p-6">
                    <div className="flex flex-col space-y-4">
                        {/* Header */}
                        <div className="flex items-center gap-3 pb-3 border-b border-gray-100 dark:border-[#383838]">
                            <div className="w-2 h-6 bg-gradient-to-b from-violet-400 to-violet-600 rounded-full"></div>
                            <div>
                                <h4 className="text-base font-semibold text-gray-800 dark:text-[#D1D2D3]">Alterar Funil</h4>
                                <p className="text-sm text-gray-600 dark:text-[#ABABAD]">Selecione o funil e estágio desejados</p>
                            </div>
                        </div>

                        {/* Campo Funil */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#D1D2D3] flex items-center gap-2">
                                <Iconify icon="mdi:funnel" className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                Funil
                            </label>
                            <Select
                                onValueChange={async (value) => {
                                    setPipelineId(value);
                                    const querys = `?pipeline_id=${value}`;
                                    await buscarConfigCrm(querys);
                                }}
                            >
                                <SelectTrigger className="h-10 border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white text-gray-900 dark:bg-[#1A1D21] dark:border-[#383838] dark:hover:border-[#565856] dark:text-[#D1D2D3]">
                                    <SelectValue placeholder="Selecione o funil" />
                                </SelectTrigger>
                                <SelectContent>
                                    {configCrmProps?.pipelines.map((option) => (
                                        <SelectItem key={option._id} value={option._id}>
                                            {option.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Campo Estágio */}
                        {pipelineId && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-[#D1D2D3] flex items-center gap-2">
                                    <Iconify icon="mdi:chart-timeline-variant" className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                    Estágio do Funil
                                </label>
                                <Select
                                    onValueChange={(value) => setPipelineStageId(value)}
                                    disabled={!pipelineId}
                                >
                                    <SelectTrigger className="h-10 border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white text-gray-900 dark:bg-[#1A1D21] dark:border-[#383838] dark:hover:border-[#565856] dark:text-[#D1D2D3]">
                                        <SelectValue placeholder="Selecione o estágio" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {configCrm?.stages.map((option) => (
                                            <SelectItem key={option._id} value={option._id}>
                                                {option.nome}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Botão Salvar */}
                        <div className="pt-3 border-t border-gray-100 dark:border-[#383838]">
                            <Button
                                disabled={!pipelineStageId}
                                className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white shadow-sm transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => onConfirm({ pipelineId, pipelineStageId })}
                                variant="default"
                            >
                                <Iconify icon="mdi:check" className="w-4 h-4 mr-2" />
                                Salvar Alterações
                            </Button>
                        </div>
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

// ----------------------------------------------------------------------
// COMPONENTE PRINCIPAL: DrawerTabNegocio
// ----------------------------------------------------------------------

DrawerTabNegocio.propTypes = {
    disabled: PropTypes.bool,
    conversationId: PropTypes.string,
    onSend: PropTypes.func
};

export default function DrawerTabNegocio({
    configCrm,
    lead,
    user,
    cliente,
    loading,
    disabled,
    changeText,
    text,
    conversationId,
    onSend,
    onOpenMenu,
    onOpenChatWhatsapp,
    callBackRefreshLead,
    callbackRefreshLeads,
    renderMode,
    ...other
}) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Estados do componente
    const [anchorMenuAlterarFunil, setAnchorMenuAlterarFunil] = useState(null);
    const [listaContatos, setListaContatos] = useState([]);
    const [listaEmpresas, setListaEmpresas] = useState([]);
    // Campanhas vêm direto da prop: guardar em estado só congelava o valor da
    // montagem (o fetch próprio está comentado abaixo e nada ressincronizava),
    // então um configCrm que chegasse depois deixava a lista vazia para sempre.

    const [editValor, setEditValor] = useState(false);
    const [loadingEnviarCrm, setLoadingEnviarCrm] = useState(false);


    // Estado do negócio
    const [negocio, setNegocio] = useState({
        nome: null,
        pipelineId: lead?.pipelineId,
        pipelineStageId: lead?.pipelineStageId,
        origemLead: lead?.origem_lead_id,
        campanhaId: lead?.campanha_id,
        statusVendaId: lead?.status_venda_id,
        tags: Array.isArray(lead?.tags) ? lead.tags.map(String) : [],
        qualificacao: lead?.qualificacao,
        dataFechamento: lead?.data_fechamento,
        contatoId: null,
        valor: formatMoedaBR(lead?.valor),
        contato: {
            nome: '',
            email: '',
            whatsapp: '',
            dataNascimento: '',
            cpf: '',
            endereco: {
                cep: '',
                logradouro: '',
                numero: '',
                complemento: '',
                bairro: '',
                cidade: '',
                estado: ''
            }
        },
        empresa: {
            nome: '',
            cnpj: '',
            endereco: {
                cep: '',
                logradouro: '',
                numero: '',
                complemento: '',
                bairro: '',
                cidade: '',
                estado: ''
            }
        }
    });

    // Estados computados
    const openAnchorAlterarFunil = Boolean(anchorMenuAlterarFunil);

    // ----------------------------------------------------------------------
    // EFEITOS
    // ----------------------------------------------------------------------

    useEffect(() => {
        if (lead?.origem_lead_id) {
            setNegocio((prev) => ({
                ...prev,
                origemLead: lead?.origem_lead_id,
            }));
        }
        // Status e tags precisam ressincronizar inclusive quando ficam vazios
        // (ex.: status removido nas configurações), então ficam fora do if acima.
        setNegocio((prev) => ({
            ...prev,
            statusVendaId: lead?.status_venda_id || null,
            tags: Array.isArray(lead?.tags) ? lead.tags.map(String) : [],
            valor: formatMoedaBR(lead?.valor),
        }));
        return () => {
            setEditValor(false);
            // Cleanup adicional para evitar interferência entre tabs
            setAnchorMenuAlterarFunil(null);
            setListaContatos([]);
            setListaEmpresas([]);
        };
    }, [lead]);

    // Carregar campanhas quando o componente for montado
    // useEffect(() => {
    //     const carregarCampanhas = async () => {
    //         try {
    //             const result = await actionListaCampanhas(cliente._id);
    //             if (result.data.data) {
    //                 setListaCampanhas(configCrm?.campanhas);
    //             }
    //         } catch (error) {
    //             console.log('Erro ao carregar campanhas:', error);
    //         }
    //     };

    //     if (cliente?._id) {
    //         carregarCampanhas();
    //     }
    // }, [cliente._id]);

    // Cleanup adicional quando o componente for desmontado
    useEffect(() => {
        return () => {
            // Limpa todos os estados locais quando o componente for desmontado
            setEditValor(false);
            setAnchorMenuAlterarFunil(null);
            setListaContatos([]);
            setListaEmpresas([]);
        };
    }, []);

    // ----------------------------------------------------------------------
    // FUNÇÕES DE PESQUISA
    // ----------------------------------------------------------------------

    const pesquisarContatos = async (pesquisa) => {
        try {
            const result = await v2_actionPesquisarContatos(cliente._id, pesquisa);
            setListaContatos(result.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const pesquisarContatosEmpresa = async (pesquisa) => {
        try {
            const result = await v2_actionPesquisarContatosEmpresa(cliente._id, pesquisa);
            setListaEmpresas(result.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const criarContatoEmpresa = async (data) => {
        const { empresa } = data;
        try {
            const contatoData = {
                clienteId: cliente._id,
                usuarioId: user.id,
                scope: {
                    nome: empresa.nome,
                    info: {
                        email: empresa.email,
                        cnpj: empresa.cnpj,
                        site: "",
                        nomeFantasia: "",
                        razaoSocial: "",
                        inscricaoEstadual: "",
                        inscricaoMunicipal: "",
                        telefone: "",
                    },
                    segmentos: [],
                    atributos: [],
                    endereco: empresa.endereco,
                    contatos: [],
                }
            };
            const result = await v2_actionCriarContatoEmpresa(contatoData);
            await pesquisarContatosEmpresa(empresa.nome);
            enqueueSnackbar(result.data.mensagem, { variant: 'success' });
        } catch (error) {
            console.log(error);
        }
    };

    // ----------------------------------------------------------------------
    // FUNÇÕES DE EDIÇÃO
    // ----------------------------------------------------------------------

    const editarLead = async (action, value) => {
        const actionMap = {
            editar_qualificacao: {
                scope: { qualificacao: Number(value) },
                message: 'Qualificação alterada com sucesso'
            },
            editar_origem_lead: {
                scope: { origem_lead_id: value },
                message: 'Origem alterada com sucesso'
            },
            editar_estagio_funil: {
                scope: { estagio_pipeline_id: value },
                message: 'Estágio alterado com sucesso'
            },
            editar_data_fechamento: {
                scope: { data_previsao: value },
                message: 'Data alterada com sucesso'
            },
            editar_chat_whatsapp: {
                scope: { whatsapp_chat_id: value },
                message: 'Chat incluído com sucesso'
            },
            editar_funil: {
                scope: value,
                message: 'Funil alterado com sucesso'
            },
            editar_valor: {
                scope: { valor: value },
                message: 'Valor alterado com sucesso'
            },
            editar_campanha: {
                scope: { campaign_id: value },
                message: 'Campanha alterada com sucesso'
            },
            editar_status_venda: {
                scope: { status_venda_id: value },
                message: 'Status da venda alterado com sucesso'
            },
            editar_tags: {
                scope: { tags: value },
                message: 'Tags alteradas com sucesso'
            }
        };

        const actionData = actionMap[action];
        if (actionData) {
            const data = { action, scope: actionData.scope };
            await v2_actionEditarLead(lead._id, data, user.id);
            enqueueSnackbar(actionData.message, { 
                variant: 'success', 
                autoHideDuration: 3000, 
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' } 
            });
        }

        callbackRefreshLeads();
        callBackRefreshLead();
    };

    // ----------------------------------------------------------------------
    // FUNÇÕES AUXILIARES
    // ----------------------------------------------------------------------

    const handleCloseMenuCanaisConvera = () => {
        setAnchorMenuCanaisConversa(null);
    };

    const handleAlterarFunil = (value) => {
        const data = {
            crm_pipeline_id: value?.pipelineId,
            estagio_pipeline_id: value?.pipelineStageId
        };
        editarLead("editar_funil", data);
        callbackRefreshLeads();
        callBackRefreshLead();
        setAnchorMenuAlterarFunil(null);
    };

    const handleAlterarEstagio = async (value) => {
        if (lead?.closed) {
            enqueueSnackbar('Não é possível alterar, reabra o lead antes de continuar', { 
                variant: 'error', 
                autoHideDuration: 3000, 
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' } 
            });
            return;
        }
        // editarLead já refaz o fetch do lead e da lista ao final; não repetir aqui.
        try {
            await editarLead("editar_estagio_funil", value);
        } catch (error) {
            console.log(error);
            enqueueSnackbar('Não foi possível alterar o estágio. Tente novamente.', {
                variant: 'error',
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            });
            // Refaz o fetch do lead para reverter o passo otimista do stepper ao estágio real.
            callBackRefreshLead();
        }
    };

    const handleAlterarOrigem = async (value) => {
        setNegocio({ ...negocio, origemLead: value });
        await editarLead("editar_origem_lead", value);
        callbackRefreshLeads();
    };



    const handleAlterarValor = async () => {
        const valorNumerico = parseMoedaBR(negocio?.valor);
        await editarLead("editar_valor", valorNumerico);
        setNegocio((prev) => ({ ...prev, valor: formatMoedaBR(valorNumerico) }));
        callbackRefreshLeads();
        setEditValor(false);
    };

    const handleAlterarCampanha = async (value) => {
        setNegocio({ ...negocio, campaign_id: value });
        await editarLead("editar_campanha", value);
        callbackRefreshLeads();
    };

    const handleAlterarStatusVenda = async (value) => {
        setNegocio({ ...negocio, statusVendaId: value });
        await editarLead("editar_status_venda", value);
        callbackRefreshLeads();
    };

    const handleAlterarTags = async (values) => {
        setNegocio({ ...negocio, tags: values });
        await editarLead("editar_tags", values);
        callbackRefreshLeads();
    };

    const handleSelecionarChat = async (value) => {
        await editarLead("editar_chat_whatsapp", value?._id);
        dispatch(getMensagensWhatsappDb(value?.sessao, value?.numeroCliente));
        dispatch(getChatInfoByChatId(value?._id, value?.funil_id, user.id, true));
        callBackRefreshLead();
        setAnchorMenuCanaisConversa(null);
    };

    const handleDesvincularChat = async () => {
        await editarLead("editar_chat_whatsapp", null);
        callBackRefreshLead();
        callbackRefreshLeads();
        dispatch(setChatInfo(null));
        dispatch(limparChats());
    };

    const handleEnviarCrm = async () => {
        try {
            setLoadingEnviarCrm(true);
            let data = {
                action: "enviar_crm",
                scope: {
                    sendCrm: true,
                }
            }
            await v2_actionEditarLead(lead._id, data, user.id);
            enqueueSnackbar('Negócio enviado ao CRM com sucesso', { variant: 'success', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
            callbackRefreshLeads();
        } catch (error) {
            console.log(error);
            enqueueSnackbar('Erro ao enviar ao CRM', { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
        } finally {
            setLoadingEnviarCrm(false);
        }
    };

    // ----------------------------------------------------------------------
    // CÁLCULOS E DERIVADOS
    // ----------------------------------------------------------------------

    const newStagesPipelines = configCrm?.stages.map((i, index) => ({
        ...i,
        posicao: index
    }));

    const ativeStepFilter = newStagesPipelines?.filter((x) => x._id === lead?.estagio_pipeline_id);
    const posicaoStage = ativeStepFilter?.[0]?.posicao;

    // ----------------------------------------------------------------------
    // RENDERIZAÇÃO
    // ----------------------------------------------------------------------

    // Modo "info-only": renderiza apenas os campos de informação (sem stages, sem wrapper)
    if (renderMode === 'info-only') {
        // Todos os controles compartilham o mesmo estilo/altura para as linhas
        // ficarem no mesmo ritmo vertical e o texto alinhar na mesma baseline.
        // A altura fica separada porque o MultiSelect precisa crescer com os
        // chips (`h-auto`): fixar h-9 nele cortaria as tags que quebram linha.
        const controleBase = 'w-full text-sm border-gray-200 bg-white text-gray-900 rounded-lg hover:border-gray-300 transition-colors dark:bg-[#1A1D21] dark:border-[#383838] dark:hover:border-[#565856] dark:text-[#D1D2D3]';
        const controle = `${controleBase} h-9`;

        // Cadastro ainda vazio: em vez de um select que não abre nada, a linha
        // mostra o atalho para a tela onde a opção é criada.
        const origens = configCrm?.origens || [];
        const statusVenda = configCrm?.statusVenda || [];
        const tagsNegocio = configCrm?.tagsNegocio || [];
        const campanhas = configCrm?.campanhas || [];

        return (
            <div className="flex flex-col gap-2">
                <NegocioInfoRow label="Origem do Lead">
                    {origens.length === 0 ? (
                        <SemOpcoesCta label="Cadastrar origens de contato" href={`${CONFIG_FUNIL}?tab=origens`} />
                    ) : (
                        <Select disabled={lead?.closed} value={negocio?.origemLead || ""} onValueChange={handleAlterarOrigem}>
                            <SelectTrigger className={controle}>
                                {negocio?.origemLead ? origens.find((o) => o._id === negocio?.origemLead)?.name : "Selecione a origem"}
                            </SelectTrigger>
                            <SelectContent>
                                {origens.map((option) => (
                                    <SelectItem key={option._id} value={option._id}>{option.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </NegocioInfoRow>

                <NegocioInfoRow label="Status da Venda">
                    {statusVenda.length === 0 ? (
                        <SemOpcoesCta label="Cadastrar status de venda" href={`${CONFIG_FUNIL}?tab=lead-status`} />
                    ) : (
                        <Select disabled={lead?.closed} value={negocio?.statusVendaId || ""} onValueChange={handleAlterarStatusVenda}>
                            <SelectTrigger className={controle}>
                                {negocio?.statusVendaId ? statusVenda.find((s) => s._id === negocio?.statusVendaId)?.nome : "Selecione o status"}
                            </SelectTrigger>
                            <SelectContent>
                                {statusVenda.map((option) => (
                                    <SelectItem key={option._id} value={option._id}>{option.nome}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </NegocioInfoRow>

                <NegocioInfoRow label="Tags">
                    {tagsNegocio.length === 0 ? (
                        <SemOpcoesCta label="Cadastrar tags de negociação" href={`${CONFIG_FUNIL}?tab=lead-tags`} />
                    ) : (
                        <MultiSelect
                            // MultiSelect é não-controlado (lê defaultValue só na montagem);
                            // a key remonta ao trocar de card para não exibir as tags do lead anterior.
                            key={`tags-${lead?._id}`}
                            disabled={lead?.closed}
                            options={tagsNegocio.map((tag) => ({ value: tag._id, label: tag.nome }))}
                            defaultValue={negocio?.tags || []}
                            onValueChange={handleAlterarTags}
                            placeholder="Selecione tags"
                            variant="default"
                            maxCount={3}
                            className={`${controleBase} min-h-9 h-auto`}
                        />
                    )}
                </NegocioInfoRow>

                <NegocioInfoRow label="Campanha">
                    {campanhas.length === 0 ? (
                        <SemOpcoesCta label="Cadastrar campanhas" href={CADASTRO_CAMPANHAS} />
                    ) : (
                        <Select disabled={lead?.closed} value={negocio?.campanhaId || ""} onValueChange={handleAlterarCampanha}>
                            <SelectTrigger className={controle}>
                                {negocio?.campanhaId ? campanhas.find((c) => c._id === negocio?.campanhaId)?.nome : "Selecione a campanha"}
                            </SelectTrigger>
                            <SelectContent>
                                {campanhas.map((campanha) => (
                                    <SelectItem key={campanha._id} value={campanha._id}>{campanha.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </NegocioInfoRow>

                <NegocioInfoRow label="Valor">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium pointer-events-none dark:text-[#8b8d90]">R$</span>
                            <Input
                                disabled={lead?.closed}
                                inputMode="decimal"
                                className={`${controle} pl-9`}
                                value={negocio?.valor || ''}
                                onChange={(e) => { setNegocio({ ...negocio, valor: maskMoedaBR(e.target.value) }); setEditValor(true); }}
                                onBlur={() => {
                                    if (negocio?.valor) {
                                        setNegocio((prev) => ({ ...prev, valor: formatMoedaBR(prev.valor) }));
                                    }
                                }}
                                placeholder="0,00"
                            />
                        </div>
                        {editValor && (
                            <Button
                                className="bg-emerald-500 hover:bg-emerald-600 h-9 w-9 p-0 shrink-0 text-white rounded-lg shadow-sm shadow-emerald-500/20 transition-all duration-200 hover:shadow-md hover:shadow-emerald-500/30"
                                onClick={handleAlterarValor}
                            >
                                <Iconify icon="mdi:check" className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </NegocioInfoRow>
            </div>
        );
    }

    // Modo padrão: renderiza stages do funil inline
    return (
        <div className="px-4 my-2 py-4 border rounded-lg bg-gray-50 dark:bg-[#1A1D21] border-gray-200 dark:border-[#383838]" {...other}>
            <div className="flex items-center gap-3">
                <MenuAlterarFunil
                    onConfirm={handleAlterarFunil}
                    configCrmProps={configCrm}
                    cliente={cliente}
                    user={user}
                    onOpen={() => setAnchorMenuAlterarFunil(true)}
                    onClose={() => setAnchorMenuAlterarFunil(null)}
                />
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[13px] font-semibold text-gray-800 truncate max-w-[140px] dark:text-[#D1D2D3]">
                        {lead?.nomeFunil || 'Sem funil'}
                    </span>
                </div>
                <Separator orientation="vertical" className="h-4 bg-gray-200 dark:bg-[#383838]" />
                <div className="flex-1 min-w-0">
                    <DrawerNegocioStagesPipeline
                        activeStepProps={posicaoStage}
                        onChange={handleAlterarEstagio}
                        disabled={lead?.closed}
                        estagios={configCrm?.stages && newStagesPipelines}
                    />
                </div>
            </div>
        </div>
    );
}
